import { slowConnectionDetector } from '@/lib/slowConnectionDetector'

/**
 * Stale time for cache-first background reads when connection is OK.
 * After this period, React Query fires a non-blocking background refetch
 * on mount. Cached data renders immediately; the refetch updates the UI
 * seamlessly when fresh data arrives.
 *
 * When the slow connection detector reports isSlow(), background reads
 * are disabled entirely (staleTime: Infinity) to avoid flooding the
 * degraded connection. Mercure SSE still pushes real-time updates.
 */
const CACHE_FIRST_STALE_TIME = 5_000

/**
 * Wraps query options with cache-first behavior.
 *
 * Always active — the app serves cached data instantly and refetches
 * in the background. This makes every page load fast and keeps the
 * app functional when connectivity is poor or absent.
 *
 * - Connection OK: stale-while-revalidate (30s stale time).
 * - Slow connection: cache-only, no background reads.
 */
export function withOfflineMode<
  T extends { staleTime?: number; refetchOnMount?: boolean | 'always' },
>(options: T): T {
  // When the connection is actively slow, skip background refetches entirely.
  // Mercure SSE + pull-to-refresh are the only cache update mechanisms.
  if (slowConnectionDetector.isSlow()) {
    return {
      ...options,
      staleTime: Infinity,
      refetchOnMount: false,
    }
  }

  // Connection is OK (or not yet measured): allow background SWR.
  return {
    ...options,
    staleTime: CACHE_FIRST_STALE_TIME,
    refetchOnMount: true,
  }
}
